import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { DomainVisitors } from '@/modules/Domains';
import { DEFAULT_REDIRECT_STATUS_CODE, RedirectEndpointEnum, RedirectMatchModeEnum } from '@/modules/Sites';

import {
  ExternalRedirectFormFields,
  ExternalRedirectFormInputs,
  ExternalRedirectTargetType,
  ExternalRedirectsQueryKeys,
  useCreateExternalRedirectsMutation,
} from '@/modules/ExternalRedirects';

import { ExternalRedirectsFormView } from './ExternalRedirectsFormView';
import { normalizeExternalRedirectPayload } from './normalize';
import { externalRedirectFormValidationSchema } from './validation';

interface Props {
  siteId: string;
}

export const ExternalRedirectsCreateFormContainer: FC<Props> = memo(({ siteId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<ExternalRedirectFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof ExternalRedirectFormInputs>>(null);
  const [targetSiteId, setTargetSiteId] = useState<string | undefined>(undefined);

  const { mutate: createExternalRedirectMutation, isLoading: createExternalRedirectIsLoading } =
  useCreateExternalRedirectsMutation(siteId)({
    onSuccess: async (data) => {
      appMessageApi.success('Зовнішній редірект успішно створено!');

      navigate(AppRoutes.ExternalRedirectsById.replace(':siteId', siteId).replace(':externalRedirectId', data.id));

      await Promise.all([
        queryClient.refetchQueries([ExternalRedirectsQueryKeys.GetAll]),
        queryClient.refetchQueries([ExternalRedirectsQueryKeys.Conflicts]),
      ]);
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof ExternalRedirectFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: ExternalRedirectFormFields = {
    siteId,
    fromDomainId: undefined,
    sourceType: RedirectEndpointEnum.Path,
    sourceNodeId: undefined,
    sourcePath: undefined,
    toType: ExternalRedirectTargetType.SystemDomain,
    toDomainId: undefined,
    toExternalDomain: undefined,
    toSiteId: undefined,
    targetType: RedirectEndpointEnum.Path,
    targetNodeId: undefined,
    targetPath: undefined,
    statusCode: DEFAULT_REDIRECT_STATUS_CODE,
    matchMode: RedirectMatchModeEnum.Exact,
    forVisitor: DomainVisitors.Any,
    isActive: true,
  };

  const handleFinish = useCallback((values: ExternalRedirectFormFields): void => {
    setFormErrors(null);
    createExternalRedirectMutation(normalizeExternalRedirectPayload(values));
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.ExternalRedirects.replace(':siteId', siteId));
  }, [siteId]);

  return (
    <ExternalRedirectsFormView
      formName="external-redirect-create"
      formType="create"
      form={ form }
      formErrors={ formErrors }
      initialValues={ initialValues }
      isFormLoading={ createExternalRedirectIsLoading }
      validationSchema={ externalRedirectFormValidationSchema }
      siteId={ siteId }
      targetSiteId={ targetSiteId }
      handleChangeTargetSiteId={ setTargetSiteId }
      handleBack={ handleBack }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
    />
  );
});
